'use client';

import {
  Box,
  Flex,
  Heading,
  IconButton,
  Modal,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MdClear, MdArrowBack } from 'react-icons/md';
import { useAppStore } from '~/store/useAppStore';
import { NestedHome, NestedViewing } from '@habity/types';
import { getStreet } from '@habity/services';
import { Viewings } from '../../homes/profile/components/price/Viewings';
import { useState, useEffect } from 'react';
import { ViewingSlotSelector } from '../../homes/profile/components/price/ViewingSlotSelector';
import { useViewing } from '~/hooks/useViewing';
import { useUser } from '~/hooks/useUser';

interface ViewingModalProps {
  home?: NestedHome;
  isOpen: boolean;
  onClose: () => void;
  initialViewingId?: number; // Optional: jump directly to slot selection for a specific viewing
}

export const ViewingModal = ({
  home,
  isOpen,
  onClose,
  initialViewingId,
}: ViewingModalProps) => {
  const isMobile = useAppStore((state) => state.isMobile);
  const [step, setStep] = useState<'list' | 'slots'>('list');
  const [selectedViewing, setSelectedViewing] = useState<NestedViewing | null>(
    null
  );
  const [selectedSlotId, setSelectedSlotId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectorKey, setSelectorKey] = useState(0); // Key to force remount when going back from login
  const [isShowingLoginForm, setIsShowingLoginForm] = useState(false);
  const { addToViewing } = useViewing();
  const { user } = useUser();

  if (!home) return null;

  // Handle initial viewing selection (when clicking directly from home profile)
  useEffect(() => {
    if (isOpen && initialViewingId && home.viewings) {
      const viewing = home.viewings.find((v) => v.id === initialViewingId);
      if (viewing) {
        setSelectedViewing(viewing);
        setStep('slots');
      }
    }
  }, [isOpen, initialViewingId, home.viewings]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('list');
      setSelectedViewing(null);
      setSelectedSlotId('');
    }
  }, [isOpen]);

  const handleViewingSelect = (viewing: NestedViewing) => {
    // Always show slot selector for any viewing (handles both single and multiple slots, plus login)
    setSelectedViewing(viewing);
    setStep('slots');
  };

  const handleBackToList = () => {
    setStep('list');
    setSelectedViewing(null);
    setSelectedSlotId('');
    setSelectorKey(0); // Reset key when going back to list
    setIsShowingLoginForm(false);
  };

  // Handler for back button
  const handleBack = () => {
    if (
      isShowingLoginForm &&
      selectedViewing?.viewing_slots &&
      selectedViewing.viewing_slots.length > 1
    ) {
      // On login form with multiple slots: remount to go back to slot selection
      setSelectorKey((prev) => prev + 1);
      setIsShowingLoginForm(false);
    } else {
      // On slot selection or single slot: go back to viewing list
      handleBackToList();
    }
  };

  const handleSlotSelect = async (slotId: number) => {
    if (!selectedViewing) return;

    // User is logged in, proceed with booking
    setIsLoading(true);
    try {
      await addToViewing(home, selectedViewing, slotId);
      // After successful booking, go back to list
      handleBackToList();
    } catch (error) {
      console.error('Error booking viewing:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isSlotStep = step === 'slots';

  return (
    <Modal
      allowPinchZoom
      size="full"
      isOpen={isOpen}
      onClose={onClose}
      returnFocusOnClose={false}
      motionPreset={isMobile ? 'none' : 'scale'}
    >
      <ModalOverlay />
      <ModalContent backgroundColor="white">
        <Box
          w="full"
          h="80px"
          position="sticky"
          backgroundColor="white"
          boxShadow="lg"
          zIndex="1"
        >
          <Box
            maxW="800px"
            mx="auto"
            w="full"
            h="full"
            display="flex"
            alignItems="center"
            px={{ base: 4, md: 8 }}
          >
            <Flex w="full" justifyContent="space-between" align="center">
              <Heading size="md">{getStreet(home)}</Heading>
              <IconButton
                aria-label="Stäng"
                size="lg"
                icon={
                  isSlotStep ? <MdArrowBack size="30" /> : <MdClear size="30" />
                }
                variant="outline"
                onClick={isSlotStep ? handleBack : onClose}
              />
            </Flex>
          </Box>
        </Box>

        <Box p={{ base: 4, md: 8 }} maxW="800px" mx="auto" w="full">
          {step === 'list' ? (
            <VStack spacing={6} align="stretch">
              <Heading as="h2" size="lg">
                Visningar för {getStreet(home)}
              </Heading>

              {home.viewings?.length === 0 ? (
                <Text>Det finns inga kommande visningar för denna bostad.</Text>
              ) : (
                <Viewings
                  viewings={home.viewings || []}
                  home={home}
                  isModal
                  onViewingSelect={handleViewingSelect}
                />
              )}
            </VStack>
          ) : (
            <VStack spacing={6} align="stretch">
              <Heading as="h2" size="lg">
                {selectedSlotId ? 'Boka visning' : 'Välj tid för visning'}
              </Heading>

              {selectedViewing && (
                <ViewingSlotSelector
                  key={selectorKey}
                  viewing={selectedViewing}
                  home={home}
                  selectedSlotId={selectedSlotId}
                  setSelectedSlotId={setSelectedSlotId}
                  onSlotSelect={handleSlotSelect}
                  onBack={handleBackToList}
                  isLoading={isLoading}
                  requiresAuth={!user}
                  onLoginFormChange={setIsShowingLoginForm}
                />
              )}
            </VStack>
          )}

          {/* {home.responsible_broker && hasNonBookableViewing && (
            <Box mt={8} p={4} borderWidth="1px" borderRadius="md">
              <Heading as="h3" size="md" mb={2}>
                Kontakta mäklare
              </Heading>
              <Text>
                {home.responsible_broker.name}
                {home.responsible_broker.phone && (
                  <>
                    <br />
                    Tel: {home.responsible_broker.phone}
                  </>
                )}
                {home.responsible_broker.email && (
                  <>
                    <br />
                    E-post: {home.responsible_broker.email}
                  </>
                )}
              </Text>
            </Box>
          )} */}
        </Box>
      </ModalContent>
    </Modal>
  );
};
